import type React from "react";
import { useCallback, useEffect, useState, type ReactNode } from "react";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  type SelectChangeEvent,
  TextField,
  CircularProgress,
  Chip,
  Stack,
  Divider,
  Grid,
  Button,
  Tooltip,
  Autocomplete,
} from "@mui/material";
import {
  AccountBalanceWallet,
  ReceiptLong,
  TrendingUp,
  TrendingDown,
  Business,
  Person,
  MoneyOff,
  HourglassEmpty,
  AddCard,
  FileDownload,
  Print,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import axios from "axios";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as xlsx from "xlsx";
import { useNavigate } from "react-router-dom";
import DateRangeSelector from "../DateRangeSelector";

// ----------------------
// Type Definitions
// ----------------------
interface IncomeState {
  totalEntries?: number;
  totalEarning?: number;
  totalDrShares?: number;
  totalhospitalShare?: number;
  totalDiscount?: number;
  totalDues?: number;
  totalAdvance?: number;
  totalProceededAmount?: number;
  totalExpectedEarning?: number;
}

interface ExpenseState {
  totalEntries?: number;
  totalExpense?: number;
}

interface ProfitLoss {
  profitLoss?: number;
  status?: "Profit" | "Loss";
}

interface StatsData {
  incomeState?: IncomeState;
  expenseState?: ExpenseState;
  profitLoss?: ProfitLoss;
}

interface ApiResponse {
  isSuccess: boolean;
  message?: string;
  data?: StatsData;
}

// ----------------------
// UI Helpers
// ----------------------
const formatCurrency = (value?: number): string => {
  const num = value ?? 0;
  return `Rs ${num.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatNumber = (value?: number): string => {
  const num = value ?? 0;
  return num.toLocaleString("en-US");
};

function SectionHeader({
  title,
  endAdornment,
  color = "text.primary",
}: {
  title: string;
  endAdornment?: ReactNode;
  color?: string;
}) {
  return (
    <Box sx={{ mb: 1.5, mt: 2.5 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1.5}
        sx={{ mb: 0.5 }}
      >
        <Typography variant="h6" fontWeight={500} color={color}>
          {title}
        </Typography>
        {endAdornment}
      </Stack>
      <Divider />
    </Box>
  );
}

function InlineMetric({
  icon,
  label,
  value,
  color,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  color?: string;
}) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{
        px: 1.5,
        py: 0.5,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1.5,
        minHeight: 38,
        flexGrow: 1,
        height: "100%", // Ensure all cards in a row have the same height
      }}
    >
      <Box sx={{ display: "inline-flex", color, mt: "2px" }}>{icon}</Box>
      <Typography variant="caption" color="text.secondary">
        {label}:
      </Typography>
      <Typography variant="subtitle2" fontWeight={700}>
        {value}
      </Typography>
    </Stack>
  );
}

// 2. REMOVED the MetricTable component as it's no longer used

// ----------------------
// Main Component
// ----------------------
export const ExpenseIncomeTrackerTab: React.FC = () => {
  const navigate = useNavigate();
  const nowDate = new Date().toISOString().split("T")[0];
  const [dateRange, setDateRange] = useState<string>("Today");
  const [fromDate, setFromDate] = useState<string>(nowDate);
  const [toDate, setToDate] = useState<string>(nowDate);
  const [loading, setLoading] = useState<boolean>(true);
  const [statsData, setStatsData] = useState<StatsData | null>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [selectedCreatedBy, setSelectedCreatedBy] = useState<any | null>(null);

  // Add missing functions
  const handleExcelExport = () => {
    if (!statsData) {
      toast.warn("No data to export.");
      return;
    }

    // Create multiple worksheets for comprehensive export
    const workbook = xlsx.utils.book_new();

    // Summary worksheet
    const summaryData = [
      ["EXPENSE & INCOME SUMMARY REPORT"],
      [`Report Period: ${fromDate} to ${toDate}`],
      ["Generated on:", new Date().toLocaleString()],
      [""], // Empty row

      // Profit & Loss Summary
      ["PROFIT & LOSS SUMMARY"],
      ["Metric", "Value"],
      [
        "Net " + (statsData.profitLoss?.status || "Result"),
        statsData.profitLoss?.profitLoss || 0,
      ],
      ["Total Earning", statsData.incomeState?.totalEarning || 0],
      ["Total Expense", statsData.expenseState?.totalExpense || 0],
      [
        "Total Proceeded Amount",
        statsData.incomeState?.totalProceededAmount || 0,
      ],
      [
        "Total Expected Earning",
        statsData.incomeState?.totalExpectedEarning || 0,
      ],
      [""], // Empty row

      // Income Details
      ["INCOME BREAKDOWN"],
      ["Income Type", "Amount"],
      ["Hospital Share", statsData.incomeState?.totalhospitalShare || 0],
      ["Doctor Shares", statsData.incomeState?.totalDrShares || 0],
      ["Total Discount", statsData.incomeState?.totalDiscount || 0],
      ["Total Dues", statsData.incomeState?.totalDues || 0],
      ["Total Advance", statsData.incomeState?.totalAdvance || 0],
      [""], // Empty row

      // Transaction Counts
      ["TRANSACTION SUMMARY"],
      ["Transaction Type", "Count"],
      ["Income Entries", statsData.incomeState?.totalEntries || 0],
      ["Expense Entries", statsData.expenseState?.totalEntries || 0],
    ];

    const summaryWorksheet = xlsx.utils.aoa_to_sheet(summaryData);

    // Set column widths
    summaryWorksheet["!cols"] = [
      { wch: 25 }, // Column A
      { wch: 20 }, // Column B
    ];

    // Detailed breakdown worksheet
    const detailedData = [
      ["DETAILED FINANCIAL BREAKDOWN"],
      [`Period: ${fromDate} to ${toDate}`],
      [""],
      ["Category", "Subcategory", "Amount", "Percentage of Total"],

      // Income breakdown with percentages
      [
        "INCOME",
        "Hospital Share",
        statsData.incomeState?.totalhospitalShare || 0,
        (
          ((statsData.incomeState?.totalhospitalShare || 0) /
            (statsData.incomeState?.totalEarning || 1)) *
          100
        ).toFixed(2) + "%",
      ],
      [
        "INCOME",
        "Doctor Shares",
        statsData.incomeState?.totalDrShares || 0,
        (
          ((statsData.incomeState?.totalDrShares || 0) /
            (statsData.incomeState?.totalEarning || 1)) *
          100
        ).toFixed(2) + "%",
      ],
      [
        "INCOME",
        "Total Advance",
        statsData.incomeState?.totalAdvance || 0,
        (
          ((statsData.incomeState?.totalAdvance || 0) /
            (statsData.incomeState?.totalEarning || 1)) *
          100
        ).toFixed(2) + "%",
      ],
      [""],

      // Deductions
      [
        "DEDUCTIONS",
        "Total Discount",
        statsData.incomeState?.totalDiscount || 0,
        (
          ((statsData.incomeState?.totalDiscount || 0) /
            (statsData.incomeState?.totalEarning || 1)) *
          100
        ).toFixed(2) + "%",
      ],
      [
        "DEDUCTIONS",
        "Total Dues",
        statsData.incomeState?.totalDues || 0,
        (
          ((statsData.incomeState?.totalDues || 0) /
            (statsData.incomeState?.totalEarning || 1)) *
          100
        ).toFixed(2) + "%",
      ],
      [""],

      // Expenses
      [
        "EXPENSES",
        "Total Expenses",
        statsData.expenseState?.totalExpense || 0,
        (
          ((statsData.expenseState?.totalExpense || 0) /
            (statsData.incomeState?.totalEarning || 1)) *
          100
        ).toFixed(2) + "%",
      ],
      [""],

      // Summary totals
      [
        "SUMMARY",
        "Gross Income",
        statsData.incomeState?.totalEarning || 0,
        "100.00%",
      ],
      [
        "SUMMARY",
        "Total Expenses",
        statsData.expenseState?.totalExpense || 0,
        (
          ((statsData.expenseState?.totalExpense || 0) /
            (statsData.incomeState?.totalEarning || 1)) *
          100
        ).toFixed(2) + "%",
      ],
      [
        "SUMMARY",
        `Net ${statsData.profitLoss?.status || "Result"}`,
        statsData.profitLoss?.profitLoss || 0,
        (
          ((statsData.profitLoss?.profitLoss || 0) /
            (statsData.incomeState?.totalEarning || 1)) *
          100
        ).toFixed(2) + "%",
      ],
    ];

    const detailedWorksheet = xlsx.utils.aoa_to_sheet(detailedData);

    // Set column widths for detailed sheet
    detailedWorksheet["!cols"] = [
      { wch: 15 }, // Category
      { wch: 20 }, // Subcategory
      { wch: 15 }, // Amount
      { wch: 18 }, // Percentage
    ];

    // Add worksheets to workbook
    xlsx.utils.book_append_sheet(workbook, summaryWorksheet, "Summary Report");
    xlsx.utils.book_append_sheet(
      workbook,
      detailedWorksheet,
      "Detailed Breakdown"
    );

    // Save the file
    const fileName = `Expense_Income_Report_${fromDate}_to_${toDate}.xlsx`;
    xlsx.writeFile(workbook, fileName);

    toast.success(`Excel report exported successfully as ${fileName}`);
  };

  const handlePrint = () => {
    if (!statsData) {
      toast.warn("No data to print.");
      return;
    }

    const doc = new jsPDF();

    // Add title
    doc
      .setFontSize(16)
      .setFont("helvetica", "bold")
      .text(
        "Expense & Income Report",
        doc.internal.pageSize.getWidth() / 2,
        20,
        {
          align: "center",
        }
      );

    // Add date range
    doc
      .setFontSize(10)
      .setFont("helvetica", "normal")
      .text(
        `Report for period: ${fromDate} to ${toDate}`,
        doc.internal.pageSize.getWidth() / 2,
        28,
        { align: "center" }
      );

    // Profit & Loss Summary
    const profitLossData = [
      ["Metric", "Value"],
      [
        "Net " + (statsData.profitLoss?.status || "Result"),
        formatCurrency(statsData.profitLoss?.profitLoss),
      ],
      ["Total Earning", formatCurrency(statsData.incomeState?.totalEarning)],
      ["Total Expense", formatCurrency(statsData.expenseState?.totalExpense)],
      [
        "Total Proceeded Amount",
        formatCurrency(statsData.incomeState?.totalProceededAmount),
      ],
      [
        "Total Expected Earning",
        formatCurrency(statsData.incomeState?.totalExpectedEarning),
      ],
    ];

    autoTable(doc, {
      head: [profitLossData[0]],
      body: profitLossData.slice(1),
      startY: 40,
      styles: { fontSize: 10 },
      headStyles: { fillColor: [25, 118, 210] },
      margin: { left: 20, right: 20 },
    });

    // Income Details
    const incomeDetailsData = [
      ["Income Details", "Amount"],
      [
        "Hospital Share",
        formatCurrency(statsData.incomeState?.totalhospitalShare),
      ],
      ["Doctor Shares", formatCurrency(statsData.incomeState?.totalDrShares)],
      ["Total Discount", formatCurrency(statsData.incomeState?.totalDiscount)],
      ["Total Dues", formatCurrency(statsData.incomeState?.totalDues)],
      ["Total Advance", formatCurrency(statsData.incomeState?.totalAdvance)],
    ];

    autoTable(doc, {
      head: [incomeDetailsData[0]],
      body: incomeDetailsData.slice(1),
      startY: (doc as any).lastAutoTable.finalY + 15,
      styles: { fontSize: 10 },
      headStyles: { fillColor: [25, 118, 210] },
      margin: { left: 20, right: 20 },
    });

    // Transaction Counts
    const transactionCountsData = [
      ["Transaction Type", "Count"],
      ["Income Entries", formatNumber(statsData.incomeState?.totalEntries)],
      ["Expense Entries", formatNumber(statsData.expenseState?.totalEntries)],
    ];

    autoTable(doc, {
      head: [transactionCountsData[0]],
      body: transactionCountsData.slice(1),
      startY: (doc as any).lastAutoTable.finalY + 15,
      styles: { fontSize: 10 },
      headStyles: { fillColor: [25, 118, 210] },
      margin: { left: 20, right: 20 },
    });

    // Add timestamp
    doc
      .setFontSize(8)
      .setFont("helvetica", "normal")
      .text(
        `Generated on: ${new Date().toLocaleString()}`,
        20,
        doc.internal.pageSize.getHeight() - 10
      );

    doc.autoPrint();
    window.open(doc.output("bloburl"), "_blank");
  };

  // Update button style to match transaction file
  const gradientButtonStyle = {
    minWidth: { xs: "auto", md: "80px" },
    fontSize: { xs: "12px", md: "12px" },
    fontWeight: "600",
    textTransform: "none",
    borderRadius: "8px",
    background: "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
    color: "#ffffff",
    boxShadow: "0 3px 5px 2px rgba(25, 118, 210, .3)",
    transition: "all 0.3s ease-in-out",
    "&:hover": {
      background: "linear-gradient(45deg, #1565c0 30%, #1e88e5 90%)",
      boxShadow: "0 5px 15px rgba(25, 118, 210, .4)",
      transform: "translateY(-1px)",
    },
  };

  const handleDateRangeChange = (value: string) => {
    setDateRange(value);
  };

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/users/findAllUsers`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      if (response.data.isSuccess) {
        setUsers(response.data.data || []);
      }
    } catch (err: any) {
      console.error("Error fetching users:", err);
    }
  }, []);

  const fetchStatsData = useCallback(async () => {
    if (!fromDate || !toDate) return;

    setLoading(true);
    setStatsData(null);
    try {
      const params: any = { fromDate, toDate };
      if (selectedCreatedBy?._id) {
        params.userId = selectedCreatedBy._id;
      }

      const response = await axios.get<ApiResponse>(
        `${process.env.REACT_APP_ADMIN_API_URL}/income/incomeExpenseState`,
        {
          params,
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );

      if (response.data.isSuccess && response.data.data) {
        setStatsData(response.data.data);
      } else {
        toast.error(response.data.message || "Failed to fetch stats data");
      }
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "An unexpected error occurred";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, selectedCreatedBy]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    fetchStatsData();
  }, [fetchStatsData]);

  const isProfit = statsData?.profitLoss?.status === "Profit";
  const profitLossColor = isProfit ? "success.main" : "error.main";
  const profitLossIcon = isProfit ? <TrendingUp /> : <TrendingDown />;

  const incomeDetails = [
    {
      icon: <Business />,
      label: "Hospital Share",
      value: formatCurrency(statsData?.incomeState?.totalhospitalShare),
      color: "#0288d1",
    },
    {
      icon: <Person />,
      label: "Doctor Shares",
      value: formatCurrency(statsData?.incomeState?.totalDrShares),
      color: "#ed6c02",
    },
    {
      icon: <MoneyOff />,
      label: "Total Discount",
      value: formatCurrency(statsData?.incomeState?.totalDiscount),
      color: "#6a1b9a",
    },
    {
      icon: <HourglassEmpty />,
      label: "Total Dues",
      value: formatCurrency(statsData?.incomeState?.totalDues),
      color: "#ad1457",
    },
    {
      icon: <AddCard />,
      label: "Total Advance",
      value: formatCurrency(statsData?.incomeState?.totalAdvance),
      color: "#1565c0",
    },
  ];

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2.5 }, maxWidth: 1200, mx: "auto" }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip
            title="Export comprehensive expense & income data to Excel file with detailed breakdown"
            arrow
            placement="top"
          >
            <Button
              variant="contained"
              size="small"
              startIcon={<FileDownload />}
              onClick={handleExcelExport}
              sx={gradientButtonStyle}
            >
              Excel
            </Button>
          </Tooltip>
          <Tooltip
            title="Print expense & income report with summary and breakdown"
            arrow
            placement="top"
          >
            <Button
              variant="contained"
              size="small"
              startIcon={<Print />}
              onClick={handlePrint}
              sx={gradientButtonStyle}
            >
              Print
            </Button>
          </Tooltip>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            gap: 2,
            flexWrap: "wrap",
            justifyContent: "flex-end",
            flexDirection: { xs: "column", sm: "row" },
            width: { xs: "100%", sm: "auto" },
          }}
        >
          {/* <Box>
            <Typography sx={{ fontSize: "12px", fontWeight: "600", mb: 0.5 }}>
              Sort By
            </Typography>
            <Select
              size="small"
              value={sort}
              onChange={e => setSort(e.target.value)}
              sx={{ minWidth: "120px" }}
              className="custom-input"
            >
              <MenuItem value="descending">Descending</MenuItem>
              <MenuItem value="accending">Ascending</MenuItem>
            </Select>
          </Box> */}
          <Box sx={{ width: { xs: "100%", sm: "160px" } }}>
            <Typography sx={{ fontSize: "12px", fontWeight: "600", mb: 0.5 }}>
              Created By
            </Typography>
            <Autocomplete
              className="custom-input"
              size="small"
              options={users}
              sx={{
                background: "#F5F5FB",
                borderRadius: "12px",
                "& fieldset": { border: "none" },
              }}
              getOptionLabel={option => option.fullName || ""}
              value={selectedCreatedBy}
              onChange={(e, newValue) => {
                setSelectedCreatedBy(newValue);
              }}
              renderInput={params => (
                <TextField {...params} placeholder="Select User" />
              )}
              clearOnEscape
            />
          </Box>
          <DateRangeSelector
            dateRange={dateRange}
            fromDate={fromDate}
            toDate={toDate}
            onDateRangeChange={handleDateRangeChange}
            onFromDateChange={setFromDate}
            onToDateChange={setToDate}
          />
        </Box>
      </Box>

      {/* Loading State */}
      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          sx={{ height: 300 }}
        >
          <CircularProgress />
        </Box>
      ) : statsData ? (
        <Grid container spacing={3}>
          {/* Profit & Loss Summary */}
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                p: 2.5,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                height: "100%",
                bgcolor: "background.paper",
                boxShadow: 1,
              }}
            >
              <SectionHeader
                title="Profit & Loss Summary"
                color={"black"}
                endAdornment={
                  <Chip
                    size="small"
                    label={statsData?.profitLoss?.status || "—"}
                    sx={{
                      bgcolor: profitLossColor,
                      color: "#fff",
                      fontWeight: 600,
                    }}
                  />
                }
              />
              <Stack spacing={2}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 1,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ color: profitLossColor }}>{profitLossIcon}</Box>
                    <Typography
                      variant="body2"
                      color="text.primary"
                      fontWeight={500}
                    >
                      Net {statsData?.profitLoss?.status || "Result"}
                    </Typography>
                  </Box>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {formatCurrency(statsData?.profitLoss?.profitLoss)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 1,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    cursor: "pointer",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      "& .MuiTypography-root": {
                        color: "#1976d2",
                      },
                    },
                  }}
                  onClick={() => navigate("/account/invoice")}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ color: "success.main" }}>
                      <TrendingUp />
                    </Box>
                    <Typography
                      variant="body2"
                      color="text.primary"
                      fontWeight={500}
                    >
                      Total Earning
                    </Typography>
                  </Box>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {formatCurrency(statsData?.incomeState?.totalEarning)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 1,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    cursor: "pointer",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      "& .MuiTypography-root": {
                        color: "#1976d2",
                      },
                    },
                  }}
                  onClick={() => navigate("/account/expense")}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ color: "error.main" }}>
                      <TrendingDown />
                    </Box>
                    <Typography
                      variant="body2"
                      color="text.primary"
                      fontWeight={500}
                    >
                      Total Expense
                    </Typography>
                  </Box>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {formatCurrency(statsData?.expenseState?.totalExpense)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 1,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ color: "#1976d2" }}>
                      <AccountBalanceWallet />
                    </Box>
                    <Typography
                      variant="body2"
                      color="text.primary"
                      fontWeight={500}
                    >
                      Total Proceeded Amount
                    </Typography>
                  </Box>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {formatCurrency(
                      statsData?.incomeState?.totalProceededAmount
                    )}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 1,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ color: "#388e3c" }}>
                      <TrendingUp />
                    </Box>
                    <Typography
                      variant="body2"
                      color="text.primary"
                      fontWeight={500}
                    >
                      Total Expected Earning
                    </Typography>
                  </Box>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {formatCurrency(
                      statsData?.incomeState?.totalExpectedEarning
                    )}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Grid>

          {/* Income Details */}
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                p: 2.5,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                height: "100%",
                bgcolor: "background.paper",
                boxShadow: 1,
              }}
            >
              <SectionHeader title="Income Details" />
              <Stack spacing={2}>
                {incomeDetails.map((metric, index) => (
                  <Box
                    key={metric.label}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      py: 1,
                      borderBottom:
                        index < incomeDetails.length - 1 ? "1px solid" : "none",
                      borderColor: "divider",
                      cursor:
                        metric.label === "Hospital Share" ||
                        metric.label === "Doctor Shares" ||
                        metric.label === "Total Discount" ||
                        metric.label === "Total Dues" ||
                        metric.label === "Total Advance"
                          ? "pointer"
                          : "default",
                      transition:
                        metric.label === "Hospital Share" ||
                        metric.label === "Doctor Shares" ||
                        metric.label === "Total Discount" ||
                        metric.label === "Total Dues" ||
                        metric.label === "Total Advance"
                          ? "all 0.2s ease-in-out"
                          : "none",
                      "&:hover":
                        metric.label === "Hospital Share" ||
                        metric.label === "Doctor Shares" ||
                        metric.label === "Total Discount" ||
                        metric.label === "Total Dues" ||
                        metric.label === "Total Advance"
                          ? {
                              "& .MuiTypography-root": {
                                color: "#1976d2",
                              },
                            }
                          : {},
                    }}
                    onClick={
                      metric.label === "Hospital Share"
                        ? () => navigate("/account/summary")
                        : metric.label === "Doctor Shares"
                        ? () => navigate("/account/doctor-share-summary")
                        : metric.label === "Total Discount"
                        ? () => navigate("/account/invoice?ps=discount")
                        : metric.label === "Total Dues"
                        ? () => navigate("/account/invoice?ps=dues")
                        : metric.label === "Total Advance"
                        ? () => navigate("/account/invoice?ps=advance")
                        : undefined
                    }
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{ color: metric.color }}>{metric.icon}</Box>
                      <Typography
                        variant="body2"
                        color="text.primary"
                        fontWeight={500}
                      >
                        {metric.label}
                      </Typography>
                    </Box>
                    <Typography variant="subtitle2" fontWeight={700}>
                      {metric.value}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          </Grid>

          {/* Transaction Counts */}
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                p: 2.5,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                height: "100%",
                bgcolor: "background.paper",
                boxShadow: 1,
              }}
            >
              <SectionHeader title="Transaction Counts" />
              <Stack spacing={2}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 1,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ color: "#7b1fa2" }}>
                      <ReceiptLong />
                    </Box>
                    <Typography
                      variant="body2"
                      color="text.primary"
                      fontWeight={500}
                    >
                      Invoice Entries
                    </Typography>
                  </Box>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {formatNumber(statsData?.incomeState?.totalEntries)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 1,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ color: "#c2185b" }}>
                      <AccountBalanceWallet />
                    </Box>
                    <Typography
                      variant="body2"
                      color="text.primary"
                      fontWeight={500}
                    >
                      Expense Entries
                    </Typography>
                  </Box>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {formatNumber(statsData?.expenseState?.totalEntries)}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Grid>
        </Grid>
      ) : (
        // No Data State
        <Box
          sx={{
            textAlign: "center",
            mt: 4,
            p: 4,
            border: "1px dashed",
            borderColor: "divider",
            borderRadius: 2,
          }}
        >
          <ReceiptLong sx={{ fontSize: 48, color: "text.disabled", mb: 1.5 }} />
          <Typography variant="h6" gutterBottom>
            No Data Found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            There are no records for the selected date range ({fromDate} to{" "}
            {toDate}).
          </Typography>
        </Box>
      )}
    </Box>
  );
};
